var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const CHECK_INTERVAL = 60 * 1000;
const MIN_CHECK_GAP = 5 * 1000;
const PROMPT_SNOOZE = 10 * 60 * 1000;
const CACHE_PREFIX = 'app-cache-';
const CHANNEL_NAME = 'app-version';
let promptElement = null;
function normalizeBaseUrl(baseUrl) {
    const normalizedBaseUrl = baseUrl || '/';
    return normalizedBaseUrl.endsWith('/') ? normalizedBaseUrl : `${normalizedBaseUrl}/`;
}
function createAppUrl(baseUrl, filename) {
    const appBaseUrl = new URL(normalizeBaseUrl(baseUrl), window.location.origin);
    return new URL(filename, appBaseUrl).toString();
}
function isVersionResponse(value) {
    return (typeof value === 'object' &&
        value !== null &&
        'version' in value &&
        typeof value.version === 'string');
}
function isChunkLoadError(reason) {
    const message = reason instanceof Error ? reason.message : String(reason || '');
    return (message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('Loading chunk') ||
        message.includes('ChunkLoadError'));
}
function isScriptLoadError(event) {
    const target = event.target;
    return target instanceof HTMLScriptElement && target.src.includes('/assets/js/');
}
function closePrompt() {
    promptElement === null || promptElement === void 0 ? void 0 : promptElement.remove();
    promptElement = null;
}
function showUpdatePrompt(onReload, onSnooze) {
    if (promptElement) {
        return;
    }
    if (!document.body) {
        if (window.confirm('检测到新版本，是否立即刷新？')) {
            onReload();
        }
        else {
            onSnooze();
        }
        return;
    }
    const style = document.createElement('style');
    style.textContent = `
.app-version-prompt {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100vw - 32px);
  padding: 12px 14px;
  color: #1f2a37;
  background: #fff;
  border: 1px solid #d9e2ec;
  border-radius: 6px;
  box-shadow: 0 12px 28px rgba(17, 24, 39, 0.18);
  font-size: 14px;
  line-height: 20px;
}
.app-version-prompt__text {
  min-width: 0;
  font-weight: 500;
  white-space: nowrap;
}
.app-version-prompt__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}
.app-version-prompt__button {
  height: 30px;
  padding: 0 12px;
  border: 1px solid #c8d3df;
  border-radius: 4px;
  background: #fff;
  color: #344054;
  cursor: pointer;
  font-size: 13px;
}
.app-version-prompt__button--primary {
  border-color: #2b6cb0;
  background: #2b6cb0;
  color: #fff;
}
@media (max-width: 640px) {
  .app-version-prompt {
    top: 12px;
    right: 12px;
    left: 12px;
    justify-content: space-between;
  }
  .app-version-prompt__text {
    white-space: normal;
  }
}
`;
    const container = document.createElement('div');
    container.className = 'app-version-prompt';
    container.setAttribute('role', 'alert');
    const text = document.createElement('div');
    text.className = 'app-version-prompt__text';
    text.textContent = '检测到新版本';
    const actions = document.createElement('div');
    actions.className = 'app-version-prompt__actions';
    const reloadButton = document.createElement('button');
    reloadButton.type = 'button';
    reloadButton.className = 'app-version-prompt__button app-version-prompt__button--primary';
    reloadButton.textContent = '立即刷新';
    const laterButton = document.createElement('button');
    laterButton.type = 'button';
    laterButton.className = 'app-version-prompt__button';
    laterButton.textContent = '稍后';
    reloadButton.addEventListener('click', () => {
        closePrompt();
        onReload();
    });
    laterButton.addEventListener('click', () => {
        closePrompt();
        onSnooze();
    });
    actions.append(reloadButton, laterButton);
    container.append(text, actions);
    document.head.append(style);
    document.body.append(container);
    promptElement = container;
}
function clearAppCacheStorage(cachePrefix) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!('caches' in window)) {
            return;
        }
        const cacheKeys = yield window.caches.keys();
        yield Promise.all(cacheKeys
            .filter(cacheKey => cacheKey.startsWith(cachePrefix))
            .map(cacheKey => window.caches.delete(cacheKey)));
    });
}
function registerServiceWorker(baseUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!('serviceWorker' in navigator) || !window.isSecureContext) {
            return;
        }
        const appBaseUrl = new URL(normalizeBaseUrl(baseUrl), window.location.origin);
        const scriptUrl = new URL('sw.js', appBaseUrl).toString();
        const registration = yield navigator.serviceWorker.register(scriptUrl, {
            scope: appBaseUrl.pathname,
            updateViaCache: 'none'
        });
        yield registration.update();
    });
}
export function setupAppVersionCheck(options) {
    const { currentVersion, baseUrl, enabled = true, checkIntervalMs = CHECK_INTERVAL, minCheckGapMs = MIN_CHECK_GAP, promptSnoozeMs = PROMPT_SNOOZE, cachePrefix = CACHE_PREFIX } = options;
    if (!enabled || !currentVersion || typeof window === 'undefined') {
        return;
    }
    const versionUrl = createAppUrl(baseUrl, 'version.json');
    const channel = 'BroadcastChannel' in window ? new BroadcastChannel(CHANNEL_NAME) : null;
    let reloading = false;
    let lastCheckAt = 0;
    let snoozedUntil = 0;
    function getRemoteVersion() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${versionUrl}?t=${Date.now()}`, {
                cache: 'no-store'
            });
            if (!response.ok) {
                return null;
            }
            const data = yield response.json();
            return isVersionResponse(data) ? data.version : null;
        });
    }
    function reloadApp() {
        return __awaiter(this, arguments, void 0, function* (reloadOptions = {}) {
            var _a, _b;
            if (reloading) {
                return;
            }
            reloading = true;
            if (reloadOptions.broadcast !== false) {
                channel === null || channel === void 0 ? void 0 : channel.postMessage({ type: 'NEW_VERSION' });
            }
            (_b = (_a = navigator.serviceWorker) === null || _a === void 0 ? void 0 : _a.controller) === null || _b === void 0 ? void 0 : _b.postMessage({
                type: 'CLEAR_APP_CACHES',
                cachePrefix
            });
            try {
                yield clearAppCacheStorage(cachePrefix);
            }
            catch (_c) {
                // Cache Storage 清理失败不阻塞刷新。
            }
            window.location.reload();
        });
    }
    function snoozePrompt() {
        snoozedUntil = Date.now() + promptSnoozeMs;
    }
    function promptReload() {
        if (Date.now() < snoozedUntil) {
            return;
        }
        showUpdatePrompt(() => {
            void reloadApp();
        }, snoozePrompt);
    }
    function check() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const now = Date.now();
                if (now - lastCheckAt < minCheckGapMs) {
                    return;
                }
                lastCheckAt = now;
                const remoteVersion = yield getRemoteVersion();
                if (remoteVersion && remoteVersion !== currentVersion) {
                    promptReload();
                }
            }
            catch (_a) {
                // 网络异常、离线或临时服务端错误不打扰用户，下次继续检测。
            }
        });
    }
    channel === null || channel === void 0 ? void 0 : channel.addEventListener('message', event => {
        var _a;
        if (((_a = event.data) === null || _a === void 0 ? void 0 : _a.type) === 'NEW_VERSION') {
            void reloadApp({ broadcast: false });
        }
    });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            void check();
        }
    });
    window.addEventListener('focus', () => {
        void check();
    });
    window.addEventListener('online', () => {
        void check();
    });
    window.addEventListener('error', event => {
        if (isScriptLoadError(event)) {
            void reloadApp();
        }
    }, true);
    window.addEventListener('unhandledrejection', event => {
        if (isChunkLoadError(event.reason)) {
            void reloadApp();
        }
    });
    void registerServiceWorker(baseUrl).catch(() => {
        // 非安全上下文、脚本不存在或浏览器限制时跳过，不影响版本检测。
    });
    window.setInterval(check, checkIntervalMs);
    window.setTimeout(check, 3000);
}
